import { Box, Flex, Text } from '@chakra-ui/react';
import { Link, Modal } from '@morressier/alembic';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  flattenObjectEntries,
  formatKeyPath
} from '../helpers/articleTypeConfig.utils';

type Props = {
  value: unknown;
  omitKeys?: string[];
  modalTitle: string;
  previewLines?: number;
};

const ExpandableObjectCell: React.FC<Props> = ({
  value,
  omitKeys = [],
  modalTitle,
  previewLines = 4
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const entries = useMemo(() => {
    const flattenedEntries = flattenObjectEntries(value);
    if (!omitKeys.length) return flattenedEntries;

    const omittedKeySet = new Set(omitKeys.map(key => key.toLowerCase()));
    return flattenedEntries.filter(item => {
      const leafKey = item.keyPath.split('.').pop()?.toLowerCase() || '';
      return !omittedKeySet.has(leafKey);
    });
  }, [omitKeys, value]);

  const previewContent = useMemo(
    () =>
      entries
        .map(item => `${formatKeyPath(item.keyPath)}: ${String(item.value)}`)
        .join('\n'),
    [entries]
  );

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setShowMore(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();
    requestAnimationFrame(checkOverflow);
  }, [previewContent, previewLines]);

  if (!entries.length) {
    return <Text textStyle="textContext">-</Text>;
  }

  return (
    <>
      <Flex direction="column" gap={0} maxW="100%">
        <Flex gap={3}>
          <Box
            ref={previewRef}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: previewLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word'
            }}
          >
            {entries.map((item, idx) => (
              <Text
                key={`${item.keyPath}-${idx}`}
                textStyle="textContext"
                whiteSpace="normal"
                overflowWrap="anywhere"
                wordBreak="break-word"
              >
                <Text as="span" fontWeight="semibold">
                  {item.keyPath === 'main'
                    ? 'Title'
                    : formatKeyPath(item.keyPath)}
                  :{' '}
                </Text>
                {String(item.value)}
              </Text>
            ))}
          </Box>
        </Flex>

        {showMore ? (
          <Flex align="center" gap={2} wrap="wrap">
            <Link variant="primary" onClick={() => setIsModalOpen(true)}>
              View More
            </Link>
          </Flex>
        ) : null}
      </Flex>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        width="900px"
      >
        <Box maxH="80vh" overflowY="auto" p={5}>
          <Text textStyle="headingGroup" mb={4}>
            {modalTitle}
          </Text>

          <Flex direction="column" gap={2}>
            {entries.map((item, idx) => (
              <Text
                key={`${item.keyPath}-${idx}`}
                textStyle="textContext"
                whiteSpace="normal"
                overflowWrap="anywhere"
                wordBreak="break-word"
              >
                <Text
                  as="span"
                  fontWeight="semibold"
                  textStyle={isModalOpen ? 'headingItem' : ''}
                >
                  {formatKeyPath(item.keyPath)}:{' '}
                </Text>
                <Text textStyle={'textBody'}>{String(item.value)}</Text>
              </Text>
            ))}
          </Flex>
        </Box>
      </Modal>
    </>
  );
};

export default ExpandableObjectCell;
